import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { object, array, string } from "yup";
import { Button, Row, Space, Spinner, Form } from "@masterteam/mt-design-react";
import AdminFormModal from "../../AdminFormModal";

import GroupsPicker from "../../GroupsPicker";
import PermissionsList from "./PermissionsList";
import { ReactComponent as NewGroupIcon } from "../../../assets/images/groups.svg";

import { addToast } from "../../../helpers";
import api from "../../../providers/api";

const { Field } = Form;

const specialChars = /[|'/\[\]:<>+=,;?*@]/;

const validationSchema = object().shape({
  Title: string()
    .required(" ")
    .test(
      "Title shouldn't contain characters",
      " ",
      (value) => !specialChars.test(value)
    ),
  Description: string().required(" "),
  Users: array().min(1, " ")
});

const cleanMatrices = (permissions, metrics) => {
  const metPermissions = metrics.map((item) => ({
    ...item.Resource,
    Role: item.Role?.ID === 2 ? { ID: 2, Role: "Access + Control" } : item.Role
  }));
  return permissions.map((item) => {
    return metPermissions.find((perm) => perm.ID === item.ID) || item;
  });
};

function AddGroup({ selectedGroup, showModal, closeHandler, callback }) {
  const intl = useIntl();
  const editMode = !!selectedGroup;
  const [initialState, setInitialState] = useState({
    Title: "",
    Description: "",
    Users: [],
    Matrices: []
  });
  const [users, setUsers] = useState([]);
  const [groupLoading, setGroupLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(true);

  function addGroup(values, { setIsSubmitting }) {
    const body = {
      ...values,
      Matrices: values.Matrices.filter((item) => item.Role).map((item) => {
        return { Resource: { ID: item.ID }, Role: { ID: item.Role.ID } };
      })
    };
    const service = editMode ? "update" : "create";
    api.group[service](body)
      .then((res) => {
        const { StatusCode, Data, StatusMessage } = res;
        if (StatusCode === "Success") {
          if (!Data || StatusMessage?.includes("The user is already exist")) {
            addToast.error("user-management.user-exist");
          } else {
            addToast.success("user-management.user-added-successfuly");
            // Data.IsDeletable = true;
            callback(Data, editMode);
          }
        } else {
          addToast.error(StatusMessage, {
            toastId: "get-users-error"
          });
        }
      })
      .catch((error) => {
        addToast.error(error.message, {
          toastId: "get-users-error"
        });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  }

  //this will get all the groups
  function getUsers() {
    api.user
      .getAll()
      .then((res) => {
        const { StatusCode, Data } = res;
        if (StatusCode === "Success") {
          setUsers(Data);
          setUsersLoading(false);
        }
      })
      .catch((error) => {
        addToast.error(error.message, {
          toastId: "get-users-error"
        });
      });
  }

  function getInitialData() {
    setGroupLoading(true);
    const apis = [api.user.getResources()];
    if (editMode) apis.push(api.group.getById(selectedGroup.ID));
    Promise.all(apis)
      .then(([permRes, groupRes]) => {
        let perms = [];
        if (permRes.StatusCode === "Success") {
          perms = permRes.Data;
          setInitialState((currentState) => ({
            ...currentState,
            Matrices: perms
          }));
        }
        if (groupRes?.StatusCode === "Success") {
          const { Data } = groupRes;
          setInitialState({
            ...Data,
            Matrices: !Data.Matrices?.length
              ? perms
              : cleanMatrices(perms, Data.Matrices)
          });
        }
      })
      .catch((error) => {
        addToast.error(error.message, {
          toastId: "get-users-error"
        });
      })
      .finally(() => {
        setGroupLoading(false);
      });
  }

  useEffect(() => {
    getInitialData();
    getUsers();
  }, []);

  return (
    <AdminFormModal
      isOpen={showModal}
      onClose={closeHandler}
      title={intl.formatMessage({ id: "common.group" })}
      isLoading={groupLoading}
      Icon={NewGroupIcon}
    >
      <Form
        initialValues={initialState}
        validationSchema={validationSchema}
        onSubmit={addGroup}
      >
        {({ values, isSubmitting, isValid, setFieldValue, errors }) => (
          <>
            <div className="user-replace-form-container add-user-form">
              <Space direction="vertical" size="medium" className="form-body">
                <Space
                  direction="vertical"
                  size="medium"
                  className="form-container"
                >
                  <div className="d-flex picker-container">
                    <div className="full-width">
                      <Row size="small">
                        <Field
                          type="text"
                          name="Title"
                          disabled={values.IsReadOnly}
                          label={intl.formatMessage({ id: "group.group-name" })}
                          inputProps={{
                            placeholder: intl.formatMessage({
                              id: "group.group-name"
                            })
                          }}
                          required
                        />
                      </Row>
                      <small className="name-character">
                        {intl.formatMessage({ id: "group.group-name-warning" })}
                      </small>
                    </div>
                  </div>
                  <div className="line"></div>
                  <Field
                    type="textarea"
                    name="Description"
                    label={intl.formatMessage({ id: "common.description" })}
                    inputProps={{
                      placeholder: intl.formatMessage({
                        id: "common.description"
                      })
                    }}
                    required
                  />
                  <PermissionsList
                    items={values.Matrices}
                    disabled={values.IsReadOnly}
                    onChange={(item) => setFieldValue("Matrices", item)}
                  />
                  {!usersLoading ? (
                    <GroupsPicker
                      list={users}
                      selected={values.Users}
                      onChange={(value) => setFieldValue("Users", value)}
                      label1={intl.formatMessage({ id: "group.list-of-users" })}
                      label2={intl.formatMessage({
                        id: "group.selected-users"
                      })}
                      valueKey="UserName"
                      nameKey="DisplayName"
                      error={errors.Users}
                    />
                  ) : (
                    <Row justify="center">
                      <Spinner />
                    </Row>
                  )}
                </Space>
                <Row size="small" justify="end">
                  <Button variant="dark" onClick={closeHandler}>
                    {intl.formatMessage({ id: "common.cancel" })}
                  </Button>
                  <Button
                    loading={isSubmitting}
                    type="submit"
                    disabled={!isValid}
                    fixed
                  >
                    {intl.formatMessage({
                      id: editMode ? "common.update" : "common.add"
                    })}
                  </Button>
                </Row>
              </Space>
            </div>
          </>
        )}
      </Form>
    </AdminFormModal>
  );
}

export default AddGroup;
